import { FormValues, Gate } from './types';
import { createEventGate, deleteEventGate, getEventGates, getPartnerMembers } from './services';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AddGateModal } from './components/AddGateModal/AddGateModal';
import { Icon } from 'features/common/components/Icon';
import { IdParamsType } from 'utils/types';
import { MainHeader } from 'features/common/components/MainHeader';
import { MainLayout } from 'layouts/MainLayout';
import { ROUTES } from 'features/routes';
import { ScannersModal } from './components/ScannersModal/ScannersModal';
import { Table } from 'features/common/components/Table';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';
import { airbrake } from 'ErrorBoundary';

const GATE_COLUMN_HEADINGS = ['Gate Name', 'Scanners', 'Actions'];
const GATE_COLUMN_MIN_WIDTH = [200, 300, 100];
const GATE_GRID_COLUMNS = 'minmax(200px, 1.5fr) minmax(300px, 2fr) minmax(100px, 0.5fr)';

const Container = styled.div`
	padding: 24px;
	background: ${({ theme }) => theme.colors.black};
`;

const ActionButton = styled.button<{ variant?: 'delete' | 'manage' }>`
	background: transparent;
	border: none;
	color: ${({ theme, variant }) => (variant === 'delete' ? theme.colors.red : theme.colors.whitePearl)};
	cursor: pointer;
	padding: 6px 12px;
	font-size: 13px;
	border-radius: 4px;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	gap: 6px;

	&:hover {
		background: ${({ theme }) => theme.colors.whitePearl}11;
	}
`;

const HeaderContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 24px;
	padding: 0 24px;
`;

const Title = styled.h2`
	font-size: 18px;
	font-weight: 500;
	color: ${({ theme }) => theme.colors.whitePearl};
`;

const AddButton = styled.button`
	background: ${({ theme }) => theme.colors.black};
	color: ${({ theme }) => theme.colors.whitePearl};
	border: 1px solid ${({ theme }) => theme.colors.whitePearl}33;
	padding: 8px 16px;
	border-radius: 20px;
	cursor: pointer;
	font-size: 13px;
	display: flex;
	align-items: center;
	gap: 8px;
	transition: all 0.2s ease;

	&:hover {
		background: ${({ theme }) => theme.colors.whitePearl}11;
	}
`;

const ScannersList = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	align-items: center;
`;

const ScannerTag = styled.span`
	background: ${({ theme }) => theme.colors.whitePearl}11;
	color: ${({ theme }) => theme.colors.whitePearl};
	border: 1px solid ${({ theme }) => theme.colors.whitePearl}22;
	padding: 4px 10px;
	border-radius: 12px;
	font-size: 13px;
	display: flex;
	align-items: center;
	gap: 6px;
	max-width: 200px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;

	&::before {
		content: '';
		display: block;
		width: 6px;
		height: 6px;
		background: ${({ theme }) => theme.colors.whitePearl}77;
		border-radius: 50%;
		flex-shrink: 0;
	}
`;

const MoreScannersTag = styled(ScannerTag)`
	background: ${({ theme }) => theme.colors.whitePearl}22;
	cursor: pointer;
	transition: all 0.2s ease;

	&:hover {
		background: ${({ theme }) => theme.colors.whitePearl}33;
	}

	&::before {
		display: none;
	}
`;

const TableContainer = styled(Table.TableContainer)`
	margin: 0;
	background: ${({ theme }) => theme.colors.black};

	thead {
		tr {
			background: ${({ theme }) => theme.colors.black};
			border-bottom: 1px solid ${({ theme }) => theme.colors.whitePearl}22;
		}

		th {
			font-size: 13px;
			font-weight: 500;
			color: ${({ theme }) => theme.colors.whitePearl}99;
			text-transform: uppercase;
			letter-spacing: 0.5px;
			padding: 12px 16px;
			white-space: nowrap;
		}
	}

	tbody tr {
		background: ${({ theme }) => theme.colors.black};
		border-bottom: 1px solid ${({ theme }) => theme.colors.whitePearl}11;

		&:hover {
			background: ${({ theme }) => theme.colors.whitePearl}11;
		}

		td {
			padding: 12px 16px;
			font-size: 14px;
		}
	}
`;

const NoScannersText = styled.span`
	color: ${({ theme }) => theme.colors.whitePearl}77;
	font-size: 13px;
	font-style: italic;
`;

const ActionsContainer = styled.div`
	display: flex;
	gap: 8px;
`;

const MAX_VISIBLE_SCANNERS = 2;

export const GatesManagement = () => {
	const { push } = useHistory();
	const { t } = useTranslation();
	const { id } = useParams<IdParamsType>();
	const queryClient = useQueryClient();
	const { showNotification, TYPES } = useToastNotification();

	const [selectedGate, setSelectedGate] = useState<Gate | null>(null);
	const [isScannersModalOpen, setIsScannersModalOpen] = useState(false);
	const [isAddGateModalOpen, setIsAddGateModalOpen] = useState(false);

	const {
		data: gates,
		isLoading,
		error,
	} = useQuery<Gate[]>({
		queryKey: ['gates', id],
		queryFn: () => getEventGates(id),
	});

	const { data: partnerData, isLoading: isPartnerDataLoading } = useQuery({
		queryKey: ['partnerSettings'],
		queryFn: getPartnerMembers,
	});

	const { mutate: createGate } = useMutation({
		mutationFn: (name: string) => createEventGate(name, id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['gates', id] });
			showNotification({ message: t('Gate created successfully'), type: TYPES.SUCCESS });
			setIsAddGateModalOpen(false);
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error creating gate', context: { error } });
			showNotification({ message: t('Error creating gate'), type: TYPES.ERROR });
		},
	});

	const { mutate: deleteGate } = useMutation({
		mutationFn: (gateId: number) => deleteEventGate(id, gateId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['gates', id] });
			showNotification({ message: t('Gate deleted successfully'), type: TYPES.SUCCESS });
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error deleting gate', context: { error } });
			showNotification({ message: t('Error deleting gate'), type: TYPES.ERROR });
		},
	});

	const handleAddGate = (data: FormValues) => {
		if (data.newGateName.trim()) {
			createGate(data.newGateName.trim());
		}
	};

	const handleDeleteGate = (gateId: number) => {
		deleteGate(gateId);
	};

	const handleManageScanners = (gate: Gate) => {
		setSelectedGate(gate);
		setIsScannersModalOpen(true);
	};

	const getScannerName = (scannerId: number) => {
		const scanner = partnerData?.members.find((member: any) => member.user_id === scannerId);
		return scanner?.full_name || scanner?.email || 'Unknown Scanner';
	};

	const renderScanners = (gate: Gate) => {
		if (gate.assigned_scanners.length === 0) {
			return <NoScannersText>No scanners assigned</NoScannersText>;
		}

		const visibleScanners = gate.assigned_scanners.slice(0, MAX_VISIBLE_SCANNERS);
		const remainingCount = gate.assigned_scanners.length - MAX_VISIBLE_SCANNERS;

		return (
			<ScannersList>
				{visibleScanners.map((scanner) => (
					<ScannerTag key={`${gate.ID}-${scanner.scanner_id}`} title={getScannerName(scanner.scanner_id)}>
						{getScannerName(scanner.scanner_id)}
					</ScannerTag>
				))}
				{remainingCount > 0 && (
					<MoreScannersTag onClick={() => handleManageScanners(gate)}>+{remainingCount} more</MoreScannersTag>
				)}
			</ScannersList>
		);
	};

	// Add effect to update selectedGate when gates change
	useEffect(() => {
		if (selectedGate && gates) {
			const updatedGate = gates.find((gate) => gate.ID === selectedGate.ID);
			if (updatedGate) {
				setSelectedGate(updatedGate);
			}
		}
	}, [gates, selectedGate]);

	if (error) {
		airbrake.notify({ error: 'Error loading gates', context: { error } });
		return (
			<MainLayout>
				<MainHeader lessPadding gap="12px">
					<Icon
						cursor="pointer"
						iconName="arrowLeft"
						onClick={() => push(generatePath(ROUTES.DASHBOARD_PATH, { id }))}
					/>
					<h1>Gates Management</h1>
				</MainHeader>
				<Container>
					<div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
						Error loading gates. Please try again later.
					</div>
				</Container>
			</MainLayout>
		);
	}

	if (isLoading || isPartnerDataLoading) {
		return (
			<MainLayout>
				<MainHeader lessPadding gap="12px">
					<Icon
						cursor="pointer"
						iconName="arrowLeft"
						onClick={() => push(generatePath(ROUTES.DASHBOARD_PATH, { id }))}
					/>
					<h1>Gates Management</h1>
				</MainHeader>
				<Container>
					<div style={{ textAlign: 'center', padding: '40px' }}>Loading gates...</div>
				</Container>
			</MainLayout>
		);
	}

	return (
		<>
			<MainLayout>
				<MainHeader lessPadding gap="12px">
					<Icon
						cursor="pointer"
						iconName="arrowLeft"
						onClick={() => push(generatePath(ROUTES.DASHBOARD_PATH, { id }))}
					/>
					<h1>Gates Management</h1>
				</MainHeader>

				<Container>
					<HeaderContainer>
						<Title>Gates</Title>
						<AddButton onClick={() => setIsAddGateModalOpen(true)}>
							<Icon iconName="plus" size="M" />
							{t('Add Gate')}
						</AddButton>
					</HeaderContainer>

					<TableContainer>
						<Table>
							<thead>
								<Table.HeaderRow gridColumns={GATE_GRID_COLUMNS}>
									{GATE_COLUMN_HEADINGS.map((heading, i) => (
										<Table.Cell key={heading} minWidth={GATE_COLUMN_MIN_WIDTH[i]}>
											<Table.ColumnHeader>{heading}</Table.ColumnHeader>
										</Table.Cell>
									))}
								</Table.HeaderRow>
							</thead>
							<tbody>
								{gates?.map((gate) => (
									<Table.DataRow gridColumns={GATE_GRID_COLUMNS} key={gate.ID}>
										<Table.Cell minWidth={GATE_COLUMN_MIN_WIDTH[0]}>{gate.name}</Table.Cell>
										<Table.Cell minWidth={GATE_COLUMN_MIN_WIDTH[1]}>{renderScanners(gate)}</Table.Cell>
										<Table.Cell minWidth={GATE_COLUMN_MIN_WIDTH[2]}>
											<ActionsContainer>
												<ActionButton variant="manage" onClick={() => handleManageScanners(gate)}>
													<Icon iconName="userCheckWhite" size="M" />
													Manage
												</ActionButton>
												<ActionButton variant="delete" onClick={() => handleDeleteGate(gate.ID)}>
													<Icon iconName="trash" size="M" />
													Delete
												</ActionButton>
											</ActionsContainer>
										</Table.Cell>
									</Table.DataRow>
								))}
							</tbody>
						</Table>
					</TableContainer>
				</Container>
			</MainLayout>

			<AddGateModal isOpen={isAddGateModalOpen} onClose={() => setIsAddGateModalOpen(false)} onSave={handleAddGate} />

			{selectedGate && (
				<ScannersModal
					isOpen={isScannersModalOpen}
					onClose={() => {
						setIsScannersModalOpen(false);
						setSelectedGate(null);
					}}
					gate={selectedGate}
					eventId={id}
				/>
			)}
		</>
	);
};
